import React, { useState, useEffect, useContext } from "react";
import {
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Tooltip
} from "@material-ui/core";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Edit as EditIcon
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  statusChip: {
    fontSize: "0.75rem",
    height: 24,
  },
  activeStatus: {
    backgroundColor: "#4caf50",
    color: "white",
  },
  expiredStatus: {
    backgroundColor: "#f44336",
    color: "white",
  },
}));

const WhatsAppStatus = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  
  const [statuses, setStatuses] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [count, setCount] = useState(0);
  const [searchParam, setSearchParam] = useState("");
  
  // Modal states
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [deletingStatus, setDeletingStatus] = useState(null);
  
  // Form states
  const [statusText, setStatusText] = useState("");
  const [selectedWhatsApp, setSelectedWhatsApp] = useState("");
  const [mediaFile, setMediaFile] = useState(null);

  useEffect(() => {
    loadStatuses();
    loadWhatsApps();
  }, [searchParam, pageNumber]);

  const loadStatuses = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/whatsapp-status", {
        params: {
          searchParam,
          pageNumber,
        },
      });
      setStatuses(data.statuses);
      setHasMore(data.hasMore);
      setCount(data.count);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const loadWhatsApps = async () => {
    try {
      const { data } = await api.get("/whatsapp");
      setWhatsapps(data.filter(w => w.status === "CONNECTED"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenStatusModal = (status = null) => {
    setSelectedStatus(status);
    if (status) {
      setStatusText(status.text);
      setSelectedWhatsApp(status.whatsappId);
    } else {
      setStatusText("");
      setSelectedWhatsApp("");
      setMediaFile(null);
    }
    setStatusModalOpen(true);
  };

  const handleCloseStatusModal = () => {
    setStatusModalOpen(false);
    setSelectedStatus(null);
    setStatusText("");
    setSelectedWhatsApp("");
    setMediaFile(null);
  };

  const handleSaveStatus = async () => {
    try {
      if (!statusText.trim()) {
        toast.error("El texto del estado es obligatorio");
        return;
      }
      if (!selectedWhatsApp) {
        toast.error("Debe seleccionar una conexión de WhatsApp");
        return;
      }

      const formData = new FormData();
      formData.append("text", statusText);
      formData.append("whatsappId", selectedWhatsApp);
      if (mediaFile) {
        formData.append("media", mediaFile);
      }

      if (selectedStatus) {
        await api.put(`/whatsapp-status/${selectedStatus.id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Estado actualizado exitosamente");
      } else {
        await api.post("/whatsapp-status", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Estado creado exitosamente");
      }
      
      handleCloseStatusModal();
      loadStatuses();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteStatus = async (statusId) => {
    try {
      await api.delete(`/whatsapp-status/${statusId}`);
      toast.success("Estado eliminado exitosamente");
      loadStatuses();
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenConfirmModal = (status) => {
    setDeletingStatus(status);
    setConfirmModalOpen(true);
  };

  const handleCloseConfirmModal = () => {
    setConfirmModalOpen(false);
    setDeletingStatus(null);
  };

  const formatDate = (dateString) => {
    return format(parseISO(dateString), "dd/MM/yyyy HH:mm", { locale: es });
  };

  const getStatusChip = (status) => {
    const isExpired = new Date(status.expiresAt) < new Date();
    return (
      <Chip
        label={isExpired ? "Expirado" : "Activo"}
        className={`${classes.statusChip} ${
          isExpired ? classes.expiredStatus : classes.activeStatus
        }`}
        size="small"
      />
    );
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Estados de WhatsApp</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleOpenStatusModal()}
            startIcon={<AddIcon />}
          >
            Crear Estado
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      
      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Texto</TableCell>
              <TableCell>Conexión</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Creado</TableCell>
              <TableCell>Expira</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableRowSkeleton columns={6} />}
            {statuses.map((status) => (
              <TableRow key={status.id}>
                <TableCell>
                  <Typography variant="body2" noWrap>
                    {status.text.length > 50
                      ? `${status.text.substring(0, 50)}...`
                      : status.text}
                  </Typography>
                </TableCell>
                <TableCell>{status.whatsapp?.name || "N/A"}</TableCell>
                <TableCell>{getStatusChip(status)}</TableCell>
                <TableCell>{formatDate(status.createdAt)}</TableCell>
                <TableCell>{formatDate(status.expiresAt)}</TableCell>
                <TableCell align="center">
                  <Tooltip title="Ver">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenStatusModal(status)}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenConfirmModal(status)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Modal para crear/editar estado */}
      <Dialog
        open={statusModalOpen}
        onClose={handleCloseStatusModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedStatus ? "Ver Estado" : "Crear Nuevo Estado"}
        </DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <TextField
              autoFocus
              margin="dense"
              label="Texto del Estado"
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              value={statusText}
              onChange={(e) => setStatusText(e.target.value)}
              disabled={!!selectedStatus}
            />
          </Box>
          
          <Box mb={2}>
            <FormControl fullWidth variant="outlined" disabled={!!selectedStatus}>
              <InputLabel>Conexión de WhatsApp</InputLabel>
              <Select
                value={selectedWhatsApp}
                onChange={(e) => setSelectedWhatsApp(e.target.value)}
                label="Conexión de WhatsApp"
              >
                {whatsapps.map((whatsapp) => (
                  <MenuItem key={whatsapp.id} value={whatsapp.id}>
                    {whatsapp.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {!selectedStatus && (
            <Box mb={2}>
              <input
                accept="image/*,video/*"
                style={{ display: "none" }}
                id="media-file"
                type="file"
                onChange={(e) => setMediaFile(e.target.files[0])}
              />
              <label htmlFor="media-file">
                <Button variant="outlined" component="span" fullWidth>
                  {mediaFile ? mediaFile.name : "Seleccionar Imagen/Video (Opcional)"}
                </Button>
              </label>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStatusModal}>Cancelar</Button>
          {!selectedStatus && (
            <Button onClick={handleSaveStatus} color="primary" variant="contained">
              Crear Estado
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Modal de confirmación para eliminar */}
      <ConfirmationModal
        title="Eliminar Estado"
        open={confirmModalOpen}
        onClose={handleCloseConfirmModal}
        onConfirm={() => {
          handleDeleteStatus(deletingStatus.id);
          handleCloseConfirmModal();
        }}
      >
        ¿Está seguro que desea eliminar este estado de WhatsApp?
      </ConfirmationModal>
    </MainContainer>
  );
};

export default WhatsAppStatus;